<template>
  <div class="card mb-4">
    <div class="card-header">
      <div class="row flex-between-center">
        <div class="col-auto d-flex align-items-center pe-0">
          <h5 class="fs-6 mb-0 text-nowrap py-2 py-xl-0">Your API Keys</h5>
          &nbsp;
          <span v-if="apiKeys" class="badge badge-subtle-secondary rounded-pill d-block mt-1">{{ apiKeys.length }}</span>
        </div>
      </div>
    </div>

    <div class="card-body p-0">

      <div v-if="actionError" class="alert alert-danger border-0 d-flex align-items-center m-3" role="alert">
        <div class="bg-danger me-3 icon-item"><span class="fas fa-exclamation-circle text-white fs-6"></span></div>
        <p class="mb-0 flex-1">{{ actionError }}</p>
      </div>

      <!-- Initial load -->
      <div v-if="loading && !apiKeys" class="text-center py-5">
        <div class="spinner-border" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <!-- Empty state -->
      <div v-else-if="apiKeys && apiKeys.length === 0" class="text-center py-6 px-3">
        <div class="d-flex justify-content-center mb-3">
          <div class="d-flex flex-center bg-primary-subtle rounded-circle" style="width: 3.5rem; height: 3.5rem;">
            <span class="fas fa-key fs-4 text-primary"></span>
          </div>
        </div>
        <h6 class="mb-1">No API keys yet</h6>
        <p class="mb-0 text-700 fs-10">Create one above to start accessing the Signum API from your own scripts and integrations.</p>
      </div>

      <!-- List -->
      <div v-else-if="apiKeys && apiKeys.length > 0" class="table-responsive scrollbar">
        <table class="table table-hover table-sm table-striped fs-10 mb-0">
          <thead class="bg-200">
            <tr>
              <th class="text-900 align-middle white-space-nowrap pe-3">Label</th>
              <th class="text-900 align-middle white-space-nowrap pe-3">Key</th>
              <th class="text-900 align-middle white-space-nowrap pe-3">Permissions</th>
              <th class="text-900 align-middle white-space-nowrap pe-3">Last used</th>
              <th class="text-900 align-middle white-space-nowrap text-center">Status</th>
              <th class="no-sort"></th>
            </tr>
          </thead>
          <tbody class="list">
            <tr v-for="key in apiKeys" :key="key.id" class="btn-reveal-trigger">
              <td class="py-2 pe-4 align-middle white-space-nowrap text-truncate">
                <span class="fw-bold">{{ key.label }}</span>
              </td>
              <td class="py-2 pe-4 align-middle white-space-nowrap">
                <code>{{ key.tokenAbbreviated }}</code>
              </td>
              <td class="py-2 pe-4 align-middle white-space-nowrap">
                <span v-for="scope in key.effectiveScopes" :key="scope" class="badge rounded-pill me-1" :class="scopeBadgeClass(scope)">{{ scope }}</span>
              </td>
              <td class="py-2 pe-4 align-middle white-space-nowrap">
                <span v-if="!key.lastUsedAt" class="text-700">Never used</span>
                <span v-else :title="key.lastUsedFromIp ? ('From ' + key.lastUsedFromIp) : ''">{{ getPrettyDate(key.lastUsedAt) }}</span>
              </td>
              <td class="py-2 align-middle text-center fs-9 white-space-nowrap">
                <span v-if="key.active" class="badge rounded-pill d-block badge-subtle-success">Active<span class="ms-1 fas fa-check" data-fa-transform="shrink-2"></span></span>
                <span v-else class="badge rounded-pill d-block badge-subtle-secondary">Inactive<span class="ms-1 fas fa-pause" data-fa-transform="shrink-2"></span></span>
              </td>
              <td class="py-2 align-middle white-space-nowrap text-end">
                <div class="dropdown font-sans-serif position-static">
                  <button class="btn btn-link text-600 btn-sm dropdown-toggle btn-reveal" type="button" :id="'dropdown_api_key_'+key.id" data-bs-toggle="dropdown" data-boundary="viewport" aria-haspopup="true" aria-expanded="false">
                    <span class="fas fa-ellipsis-h fs-10"></span>
                  </button>
                  <div class="dropdown-menu dropdown-menu-end border py-0" :aria-labelledby="'dropdown_api_key_'+key.id">
                    <div class="py-2">
                      <a v-if="key.active" @click.prevent="deactivateKey(key)" class="dropdown-item text-warning" href="#!">Deactivate</a>
                      <a v-if="!key.active" @click.prevent="reactivateKey(key)" class="dropdown-item text-success" href="#!">Reactivate</a>

                      <template v-if="!key.active">
                        <div class="dropdown-divider"></div>
                        <a @click.prevent="deleteKey(key)" class="dropdown-item text-danger" href="#!">Delete</a>
                      </template>
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

    </div>
  </div>

  <SimpleModal ref="modalRef" :loading="modalLoading" />
</template>

<script>
import SimpleModal from '@/components/SimpleModal'
import Dropdown from 'bootstrap/js/dist/dropdown.js'

export default {
  components: {
    SimpleModal
  },
  props: {
    loading: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      modalLoading: false,
      actionError: null
    }
  },
  computed: {
    apiKeys () {
      return this.$store.getters.getApiKeys
    }
  },
  methods: {
    scopeBadgeClass (scope) {
      switch (scope) {
        case 'read': return 'badge-subtle-secondary'
        case 'write': return 'badge-subtle-info'
        case 'trading': return 'badge-subtle-warning'
        case 'admin': return 'badge-subtle-danger'
        default: return 'badge-subtle-secondary'
      }
    },
    getPrettyDate (someString) {
      const dateObj = new Date(Date.parse(someString))
      return dateObj.toLocaleString(undefined, { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', hour12: false, minute: '2-digit' })
    },
    async reactivateKey (key) {
      this.actionError = null
      const result = await this.$store.dispatch('updateApiKey', { id: key.id, active: true })
      if (result && !result.ok) this.actionError = result.error
    },
    deactivateKey (key) {
      this.actionError = null
      this.$refs.modalRef.setValues({
        title: 'Deactivate API Key?',
        message: 'Are you sure you want to deactivate <span class="fw-bold">"' + key.label + '"</span>?<br><br>Any script or integration using it will immediately stop working. You can reactivate it later.',
        callback: async () => {
          this.modalLoading = true
          const result = await this.$store.dispatch('updateApiKey', { id: key.id, active: false })
          this.modalLoading = false
          if (result && !result.ok) this.actionError = result.error
        }
      })
      this.$refs.modalRef.toggleModal(true)
    },
    deleteKey (key) {
      this.actionError = null
      this.$refs.modalRef.setValues({
        title: 'Delete API Key?',
        message: 'Are you sure you want to permanently delete <span class="fw-bold">"' + key.label + '"</span>?<br><br>This action cannot be undone.',
        callback: async () => {
          this.modalLoading = true
          const result = await this.$store.dispatch('deleteApiKey', key.id)
          this.modalLoading = false
          if (result && !result.ok) this.actionError = result.error
        }
      })
      this.$refs.modalRef.toggleModal(true)
    }
  },
  mounted () {
    // Initialize all dropdowns (mirrors BotList.vue)
    var dropdownElementList = [].slice.call(document.querySelectorAll('.dropdown-toggle'))
    dropdownElementList.map(function (dropdownToggleEl) {
      return new Dropdown(dropdownToggleEl)
    })
  }
}
</script>

<style scoped>
.table tr td {
  border-style: none;
}
.dropdown-menu {
  z-index: 9999;
}
</style>
